import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AlertTriangle, Copy, Eye, EyeOff, Loader2, LogIn, Mail, MoreHorizontal, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { studentApi } from '@/lib/api';
import { courseKeysApi } from '@/lib/api/courseKeys';
import config from '@/config';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/usePermissions';
import { can } from '@/utils/permissions';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { MindtapReleaseDialog } from '@/components/course-keys/MindtapReleaseDialog';
import type { MindtapReleaseKeyRow } from '@/lib/mindtapReleaseKeys';
import { MindtapEnrollmentPreviewDialog } from '@/components/course-keys/MindtapEnrollmentPreviewDialog';
import { CourseKeySearchInput } from '@/components/course-keys/CourseKeySearchInput';
import { matchesEnrollmentSearch } from '@/components/course-keys/courseKeySearch';
import type {
  CourseKeyConfig,
  CourseKeyEnrollmentRow,
  CourseKeyPlatform,
  IspringAccessStatus,
  IspringEnrollmentRow,
  MindtapEnrollmentPreview,
  MindtapEnrollmentRow,
} from '@/types/course-key';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  formatEnrollmentStartDate,
  getAdminBusinessEtStacked,
  getEasternCalendarDateIso,
} from '@/lib/admin-datetime';

export type ReleaseRow = CourseKeyEnrollmentRow & {
  configId: string;
};

type PlatformFilter = 'all' | CourseKeyPlatform;

function studentPhone(row: CourseKeyEnrollmentRow): string {
  return row.cellPhoneNumber || row.phone || '';
}

function programTitle(config: CourseKeyConfig): string {
  if (typeof config.programId === 'object' && config.programId?.title) {
    return config.programId.title;
  }
  return String(config.programId);
}

function isMindtapRow(row: ReleaseRow): row is MindtapEnrollmentRow & { configId: string } {
  return row.platform === 'mindtap';
}

type MindtapReleaseUrgency = 'today' | 'tomorrow';

function getMindtapReleaseUrgency(startDate: string): MindtapReleaseUrgency | null {
  const normalized = startDate.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return null;
  if (normalized === getEasternCalendarDateIso(0)) return 'today';
  if (normalized === getEasternCalendarDateIso(1)) return 'tomorrow';
  return null;
}

function isUrgentMindtapRelease(row: ReleaseRow): row is MindtapEnrollmentRow & { configId: string } {
  if (!isMindtapRow(row) || row.releaseStatus === 'released') return false;
  if (row.sharedKeysEnabled) return false;
  return getMindtapReleaseUrgency(row.startDate) !== null;
}

function StackedBusinessEtCell({
  value,
}: {
  value: Date | string | number | null | undefined;
}) {
  const stacked = getAdminBusinessEtStacked(value);
  if (!stacked) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }
  return (
    <div className="text-sm leading-tight text-muted-foreground">
      <div>{stacked.dateLine}</div>
      <div className="text-xs pt-1">{stacked.timeLine}</div>
    </div>
  );
}

function formatEnrollmentDeliveryMode(mode: string | null | undefined): string {
  if (!mode?.trim()) return '—';
  const kebab = mode.trim().toLowerCase();
  const labels: Record<string, string> = {
    hybrid: 'Hybrid',
    online: 'Online',
    'via-zoom': 'Via-Zoom',
    'on-campus': 'On-Campus',
    oncampus: 'On-Campus',
    zoom: 'Via-Zoom',
  };
  if (labels[kebab]) return labels[kebab];

  const upper = mode.trim().toUpperCase().replace(/-/g, '_');
  const upperLabels: Record<string, string> = {
    HYBRID: 'Hybrid',
    ONLINE: 'Online',
    ZOOM: 'Via-Zoom',
    ONCAMPUS: 'On-Campus',
    ON_CAMPUS: 'On-Campus',
    VIA_ZOOM: 'Via-Zoom',
  };
  return upperLabels[upper] ?? mode;
}

function PlatformSourceBadge({ platform }: { platform: CourseKeyPlatform }) {
  const isMindtap = platform === 'mindtap';
  return (
    <Badge
      variant="outline"
      className={cn(
        'text-[10px] font-semibold uppercase tracking-wide',
        isMindtap
          ? 'border-sky-300 bg-sky-50 text-sky-800 dark:border-sky-700 dark:bg-sky-950/50 dark:text-sky-200'
          : 'border-orange-300 bg-orange-50 text-orange-800 dark:border-orange-700 dark:bg-orange-950/50 dark:text-orange-200'
      )}
    >
      {isMindtap ? 'Mindtap' : 'iSpring'}
    </Badge>
  );
}

/** Release tab only — avoid Mindtap-style "Pending" for student self-provision flows. */
function ispringReleaseStatusDisplay(status: IspringAccessStatus) {
  switch (status) {
    case 'active':
      return <Badge className="bg-green-600 hover:bg-green-600">Active</Badge>;
    case 'ready':
      return <span className="text-sm text-muted-foreground">—</span>;
    case 'not_started':
      return <span className="text-sm text-muted-foreground">Not started</span>;
    case 'expired':
      return <Badge variant="outline">Expired</Badge>;
    case 'reactivation_required':
      return <Badge className="bg-amber-600 hover:bg-amber-600">Reactivation</Badge>;
    default:
      return <span className="text-sm text-muted-foreground">—</span>;
  }
}

async function copyStudentContact(
  fullName: string | null | undefined,
  email: string,
  phone: string | null | undefined
) {
  const text = [fullName || '—', email || '—', phone || '—'].join('\n');
  try {
    await navigator.clipboard.writeText(text);
    toast.success('Student details copied');
  } catch {
    toast.error('Could not copy student details');
  }
}

type Props = {
  active: boolean;
  configIdFilter?: string | null;
  refreshToken?: number;
  onClearConfigFilter?: () => void;
};

export function CourseKeyReleaseTab({
  active,
  configIdFilter,
  refreshToken = 0,
  onClearConfigFilter,
}: Props) {
  const { permissions, adminType } = usePermissions();
  const canImpersonate = can(permissions, 'users.main.impersonate', adminType);
  const canManage = can(permissions, 'courseKeys.main.manage', adminType);

  const [rows, setRows] = useState<ReleaseRow[]>([]);
  const [configs, setConfigs] = useState<CourseKeyConfig[]>([]);
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>('all');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [releaseTarget, setReleaseTarget] = useState<
    (MindtapEnrollmentRow & { configId: string }) | null
  >(null);
  const [releaseInitialRows, setReleaseInitialRows] = useState<MindtapReleaseKeyRow[] | null>(
    null
  );
  const [releaseDialogLoading, setReleaseDialogLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<MindtapEnrollmentPreview | null>(null);
  const [previewTitle, setPreviewTitle] = useState('Release overview');
  const [impersonateTarget, setImpersonateTarget] = useState<{
    studentId: string;
    studentName: string;
  } | null>(null);
  const [impersonatingUserId, setImpersonatingUserId] = useState<string | null>(null);
  const [resendTarget, setResendTarget] = useState<ReleaseRow | null>(null);
  const [resendingOrderId, setResendingOrderId] = useState<string | null>(null);
  const [applyExtensionTarget, setApplyExtensionTarget] = useState<ReleaseRow | null>(null);
  const [applyingExtensionOrderId, setApplyingExtensionOrderId] = useState<string | null>(null);
  const [visibilityUpdatingOrderId, setVisibilityUpdatingOrderId] = useState<string | null>(null);
  const [urgentOnlyFilter, setUrgentOnlyFilter] = useState(false);

  const filterButtons: { id: PlatformFilter; label: string }[] = [
    { id: 'all', label: 'All Keys' },
    { id: 'ispring', label: 'iSpring Keys' },
    { id: 'mindtap', label: 'MindTap Keys' },
  ];

  const load = useCallback(
    async (silent = false) => {
      if (!active) return;
      try {
        if (silent) setRefreshing(true);
        else setLoading(true);

        const listData = await courseKeysApi.list({
          page: 1,
          limit: 100,
          platform: platformFilter === 'all' ? undefined : platformFilter,
          status: 'active',
        });
        const activeConfigs = listData.items || [];
        setConfigs(activeConfigs);

        const targets = configIdFilter
          ? activeConfigs.filter((c) => c._id === configIdFilter)
          : activeConfigs;

        const enrollmentBatches = await Promise.all(
          targets.map(async (config) => {
            const enrollments = await courseKeysApi.listConfigEnrollments(config._id);
            return enrollments.map((row) => ({ ...row, configId: config._id }));
          })
        );

        setRows(enrollmentBatches.flat());
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Failed to load enrollments');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [active, configIdFilter, platformFilter]
  );

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  const filteredConfig = useMemo(
    () => (configIdFilter ? configs.find((c) => c._id === configIdFilter) : null),
    [configs, configIdFilter]
  );

  const hasSharedKeysModeInView = useMemo(
    () =>
      rows.some(
        (row) => isMindtapRow(row) && row.sharedKeysEnabled
      ),
    [rows]
  );

  const filteredRows = useMemo(
    () => rows.filter((row) => matchesEnrollmentSearch(row, searchInput)),
    [rows, searchInput]
  );

  const urgentMindtapReleases = useMemo(
    () => rows.filter((row) => isUrgentMindtapRelease(row)),
    [rows]
  );

  const urgentCounts = useMemo(() => {
    let today = 0;
    let tomorrow = 0;
    for (const row of urgentMindtapReleases) {
      const urgency = getMindtapReleaseUrgency(row.startDate);
      if (urgency === 'today') today += 1;
      else if (urgency === 'tomorrow') tomorrow += 1;
    }
    return { today, tomorrow, total: urgentMindtapReleases.length };
  }, [urgentMindtapReleases]);

  const tableRows = useMemo(() => {
    if (!urgentOnlyFilter) return filteredRows;
    return filteredRows.filter((row) => isUrgentMindtapRelease(row));
  }, [filteredRows, urgentOnlyFilter]);

  useEffect(() => {
    if (urgentMindtapReleases.length === 0 && urgentOnlyFilter) {
      setUrgentOnlyFilter(false);
    }
  }, [urgentMindtapReleases.length, urgentOnlyFilter]);

  const emptyConfigMessage = useMemo(() => {
    if (platformFilter === 'ispring') {
      return 'No active iSpring configs. Create and submit an iSpring course key first.';
    }
    if (platformFilter === 'mindtap') {
      return 'No active Mindtap configs. Create and submit a Mindtap course key first.';
    }
    return 'No active course key configs. Create and submit a course key first.';
  }, [platformFilter]);

  const openPreview = async (
    row: MindtapEnrollmentRow & { configId: string },
    includeKey: boolean,
    title: string
  ) => {
    setPreviewTitle(title);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const data = await courseKeysApi.previewMindtapEnrollment(
        row.configId,
        row.orderId,
        includeKey
      );
      setPreviewData(data);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Preview failed');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleRelease = async (keys: MindtapReleaseKeyRow[]) => {
    if (!releaseTarget) return;
    await courseKeysApi.releaseMindtapEnrollment(
      releaseTarget.configId,
      releaseTarget.orderId,
      keys
    );
    toast.success(
      releaseTarget.releaseStatus === 'released'
        ? 'Course keys updated'
        : 'Course keys released'
    );
    setReleaseTarget(null);
    setReleaseInitialRows(null);
    void load(true);
  };

  const handleToggleVisibility = async (row: MindtapEnrollmentRow & { configId: string }) => {
    const nextHidden = !row.credentialsHidden;
    try {
      setVisibilityUpdatingOrderId(row.orderId);
      await courseKeysApi.setMindtapEnrollmentVisibility(
        row.configId,
        row.orderId,
        nextHidden
      );
      toast.success(
        nextHidden
          ? 'Course key hidden from student portal'
          : 'Course key visible in student portal'
      );
      void load(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update visibility');
    } finally {
      setVisibilityUpdatingOrderId(null);
    }
  };

  useEffect(() => {
    if (!releaseTarget) {
      setReleaseInitialRows(null);
      setReleaseDialogLoading(false);
      return;
    }

    if (releaseTarget.releaseStatus !== 'released') {
      setReleaseInitialRows([{ name: '', courseKey: '' }]);
      setReleaseDialogLoading(false);
      return;
    }

    let cancelled = false;
    setReleaseDialogLoading(true);
    void courseKeysApi
      .previewMindtapEnrollment(releaseTarget.configId, releaseTarget.orderId, true)
      .then((preview) => {
        if (cancelled) return;
        const rows =
          preview.keys?.length > 0
            ? preview.keys
                .filter((key) => key.courseKey)
                .map((key) => ({
                  name: key.name,
                  courseKey: key.courseKey || '',
                }))
            : preview.courseKey
              ? [{ name: 'Course Key', courseKey: preview.courseKey }]
              : [{ name: '', courseKey: '' }];
        setReleaseInitialRows(rows);
      })
      .catch((e) => {
        if (cancelled) return;
        toast.error(e instanceof Error ? e.message : 'Failed to load existing keys');
        setReleaseTarget(null);
      })
      .finally(() => {
        if (!cancelled) setReleaseDialogLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [releaseTarget]);

  const openImpersonateConfirm = (row: ReleaseRow) => {
    if (!canImpersonate) {
      toast.error('You do not have permission to view as user.');
      return;
    }
    if (!row.studentId) {
      toast.error('Student account is not linked to this enrollment.');
      return;
    }
    if (!config.studentAppUrl) {
      toast.error('Student portal URL is not configured (VITE_STUDENT_APP_URL).');
      return;
    }
    setImpersonateTarget({
      studentId: row.studentId,
      studentName: row.studentName || row.email || 'this student',
    });
  };

  const openResendConfirm = (row: ReleaseRow) => {
    if (!canManage) {
      toast.error('You do not have permission to resend login instructions.');
      return;
    }
    if (!row.email?.trim()) {
      toast.error('This enrollment does not have a student email on file.');
      return;
    }
    setResendTarget(row);
  };

  const confirmResendLoginInstructions = async () => {
    if (!resendTarget) return;

    setResendingOrderId(resendTarget.orderId);
    try {
      const result = await courseKeysApi.resendLoginInstructions(
        resendTarget.configId,
        resendTarget.orderId
      );
      toast.success(`Login instructions sent to ${result.email}`);
      setResendTarget(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to resend login instructions');
    } finally {
      setResendingOrderId(null);
    }
  };

  const confirmApplyPaidExtension = async () => {
    if (!applyExtensionTarget) return;

    setApplyingExtensionOrderId(applyExtensionTarget.orderId);
    try {
      const result = await courseKeysApi.applyPaidIspringExtension(
        applyExtensionTarget.configId,
        applyExtensionTarget.orderId
      );
      const until = new Date(result.newDeactivationDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: applyExtensionTarget.platform === 'mindtap' ? 'UTC' : 'America/New_York',
      });
      toast.success(
        applyExtensionTarget.platform === 'mindtap'
          ? result.emailSent
            ? `MindTap access restored until ${until}. The student was emailed.`
            : `MindTap access restored until ${until}. The student email could not be sent.`
          : `iSpring access restored until ${until}`
      );
      setApplyExtensionTarget(null);
      await load(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to apply paid extension');
    } finally {
      setApplyingExtensionOrderId(null);
    }
  };

  const confirmViewAsUser = async () => {
    if (!impersonateTarget) return;

    setImpersonatingUserId(impersonateTarget.studentId);
    try {
      const res = await studentApi.impersonate(impersonateTarget.studentId);
      const baseUrl = res?.data?.impersonationUrl;
      if (!baseUrl) {
        throw new Error('No impersonation URL returned');
      }
      const returnTo = `${window.location.origin}${window.location.pathname}${window.location.search}`;
      const url = new URL(baseUrl);
      url.searchParams.set('returnTo', returnTo);
      url.searchParams.set('sessionMode', 'tab');
      window.open(url.toString(), '_blank', 'noopener,noreferrer');
      toast.success(`Opened student portal as ${impersonateTarget.studentName}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to start impersonation');
    } finally {
      setImpersonatingUserId(null);
      setImpersonateTarget(null);
    }
  };

  if (!active) return null;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>Release Key</CardTitle>
              <CardDescription>
                View enrolled students across iSpring and Mindtap course keys. Release Cengage
                course keys for Mindtap only — iSpring access is provisioned by students in the
                portal.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => void load(true)} disabled={refreshing}>
              <RefreshCw className={cn('mr-2 h-4 w-4', refreshing && 'animate-spin')} />
              Refresh
            </Button>
          </div>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="inline-flex flex-wrap gap-1 rounded-full border p-1">
              {filterButtons.map((btn) => (
                <button
                  key={btn.id}
                  type="button"
                  onClick={() => setPlatformFilter(btn.id)}
                  className={cn(
                    'rounded-full px-3 py-1.5 text-sm transition-colors',
                    platformFilter === btn.id
                      ? 'bg-foreground text-background'
                      : 'text-muted-foreground hover:bg-muted'
                  )}
                >
                  {btn.label}
                </button>
              ))}
            </div>
            <CourseKeySearchInput
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Search by student name, email, or program…"
              aria-label="Search enrollments by student name, email, or program"
            />
          </div>

          {configIdFilter && filteredConfig && (
            <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm">
              <span>
                Filtered to: <strong>{programTitle(filteredConfig)}</strong>
              </span>
              {onClearConfigFilter && (
                <Button variant="ghost" size="sm" className="h-7" onClick={onClearConfigFilter}>
                  Show all
                </Button>
              )}
            </div>
          )}

          {hasSharedKeysModeInView && (
            <Alert className="mt-4">
              <AlertTitle>Using shared keys</AlertTitle>
              <AlertDescription>
                One or more MindTap configs in this view use shared program keys. Per-student release
                is disabled for those enrollments — update keys on the course key edit form. You can
                still hide keys from individual students.
              </AlertDescription>
            </Alert>
          )}
        </CardHeader>
        <CardContent>
          {!loading && urgentCounts.total > 0 && platformFilter !== 'ispring' && (
            <Alert
              variant="destructive"
              className={cn(
                'mb-4 border-amber-500/60 bg-amber-50 text-amber-950',
                'dark:border-amber-600/50 dark:bg-amber-950/30 dark:text-amber-100',
                '[&>svg]:text-amber-600 dark:[&>svg]:text-amber-400'
              )}
            >
              <AlertTriangle className="animate-pulse" />
              <AlertTitle className="text-amber-950 dark:text-amber-50">
                Mindtap course keys needed before start date
              </AlertTitle>
              <AlertDescription className="text-amber-900/90 dark:text-amber-100/90">
                <p>
                  {urgentCounts.today > 0 && (
                    <span>
                      <strong>{urgentCounts.today}</strong> student
                      {urgentCounts.today === 1 ? '' : 's'} start <strong>today</strong>
                      {urgentCounts.tomorrow > 0 ? '; ' : ' — '}
                    </span>
                  )}
                  {urgentCounts.tomorrow > 0 && (
                    <span>
                      <strong>{urgentCounts.tomorrow}</strong> start{' '}
                      <strong>tomorrow</strong>
                      {' — '}
                    </span>
                  )}
                  Release Cengage keys now so students see them when the 8:00 AM ET login
                  email arrives.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className={cn(
                      'border-amber-700/60 font-medium shadow-sm',
                      urgentOnlyFilter
                        ? 'bg-amber-900 text-amber-50 hover:bg-amber-950 hover:text-amber-50 dark:bg-amber-100 dark:text-amber-950 dark:hover:bg-amber-50'
                        : 'bg-white text-amber-950 hover:bg-amber-100 dark:bg-amber-950 dark:text-amber-50 dark:hover:bg-amber-900'
                    )}
                    onClick={() => setUrgentOnlyFilter((prev) => !prev)}
                  >
                    {urgentOnlyFilter ? 'Show all students' : `Show ${urgentCounts.total} urgent`}
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : configs.length === 0 ? (
            <p className="text-sm text-muted-foreground">{emptyConfigMessage}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No matching enrollments yet.</p>
          ) : tableRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {urgentOnlyFilter
                ? 'No urgent Mindtap releases match your search.'
                : 'No enrollments match your search.'}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>Program</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Start date</TableHead>
                  <TableHead>Last login</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>{platformFilter === 'ispring' ? 'Provisioned Date' : 'Released Date'}</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tableRows.map((row) => {
                  const mindtap = isMindtapRow(row);
                  const ispring = !mindtap ? (row as IspringEnrollmentRow & { configId: string }) : null;
                  const urgent = isUrgentMindtapRelease(row);
                  const startUrgency = mindtap ? getMindtapReleaseUrgency(row.startDate) : null;

                  return (
                    <TableRow
                      key={`${row.configId}-${row.orderId}`}
                      className={cn(
                        urgent && 'bg-amber-50/70 dark:bg-amber-950/20'
                      )}
                    >
                      <TableCell>
                        <div className="flex min-w-[180px] items-start gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium">{row.studentName || '—'}</p>
                            <p className="text-xs text-muted-foreground">{row.email || '—'}</p>
                            <p className="text-xs text-muted-foreground">
                              {studentPhone(row) || '—'}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
                            aria-label={`Copy contact details for ${row.studentName || row.email}`}
                            onClick={() =>
                              void copyStudentContact(
                                row.studentName,
                                row.email,
                                studentPhone(row)
                              )
                            }
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>{row.programName}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {formatEnrollmentDeliveryMode(row.deliveryMode)}
                      </TableCell>
                      <TableCell>
                        <PlatformSourceBadge platform={row.platform} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        <div className="flex flex-col gap-1">
                          <span>{formatEnrollmentStartDate(row.startDate)}</span>
                          {urgent && startUrgency && (
                            <Badge
                              variant="outline"
                              className={cn(
                                'w-fit text-[10px] font-semibold uppercase',
                                startUrgency === 'today'
                                  ? 'animate-pulse border-amber-600 bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200'
                                  : 'border-amber-500/70 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200'
                              )}
                            >
                              {startUrgency === 'today' ? 'Starts today' : 'Starts tomorrow'}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <StackedBusinessEtCell value={row.lastPortalLoginAt} />
                      </TableCell>
                      <TableCell>
                        {mindtap ? (
                          <div className="flex flex-col gap-1">
                            {row.credentialsHidden ? (
                              <Badge variant="outline" className="w-fit">
                                Hidden
                              </Badge>
                            ) : row.releaseStatus === 'released' ? (
                              <Badge className="bg-green-600 hover:bg-green-600">
                                {row.keySource === 'shared'
                                  ? 'Shared keys'
                                  : row.releasedKeyCount && row.releasedKeyCount > 1
                                    ? `${row.releasedKeyCount} keys`
                                    : 'Processed'}
                              </Badge>
                            ) : (
                              <Badge variant="destructive">Pending</Badge>
                            )}
                          </div>
                        ) : ispring ? (
                          ispringReleaseStatusDisplay(ispring.accessStatus)
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {mindtap ? (
                          <StackedBusinessEtCell value={row.releasedAt} />
                        ) : (
                          <StackedBusinessEtCell value={ispring?.provisionedAt} />
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {mindtap ? (
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Preview release overview"
                              onClick={() =>
                                void openPreview(row, row.releaseStatus === 'released', 'Release overview')
                              }
                            >
                              <Eye className="h-4 w-4" />
                            </Button>

                            {row.pendingExtension ? (
                              <Button
                                size="sm"
                                disabled={!canManage || applyingExtensionOrderId === row.orderId}
                                onClick={() => setApplyExtensionTarget(row)}
                              >
                                {applyingExtensionOrderId === row.orderId
                                  ? 'Completing…'
                                  : 'Complete extension'}
                              </Button>
                            ) : row.sharedKeysEnabled ? (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled
                                title="Shared keys are managed on the course key edit form"
                              >
                                Using shared keys
                              </Button>
                            ) : row.releaseStatus === 'released' ? (
                              <Button
                                size="sm"
                                className="bg-green-600 text-white hover:bg-green-600/90"
                                disabled
                              >
                                {row.releasedKeyCount && row.releasedKeyCount > 1
                                  ? `${row.releasedKeyCount} keys`
                                  : 'Processed'}
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => setReleaseTarget(row)}
                              >
                                Release
                              </Button>
                            )}

                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" aria-label="More actions">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canManage && (
                                  <DropdownMenuItem
                                    disabled={resendingOrderId === row.orderId}
                                    onClick={() => openResendConfirm(row)}
                                  >
                                    <Mail className="mr-2 h-4 w-4" />
                                    Resend Login Instructions
                                  </DropdownMenuItem>
                                )}
                                {canManage && (
                                  <DropdownMenuItem
                                    disabled={visibilityUpdatingOrderId === row.orderId}
                                    onClick={() => void handleToggleVisibility(row)}
                                  >
                                    {row.credentialsHidden ? (
                                      <>
                                        <Eye className="mr-2 h-4 w-4" />
                                        Show in student portal
                                      </>
                                    ) : (
                                      <>
                                        <EyeOff className="mr-2 h-4 w-4" />
                                        Hide from student portal
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                )}
                                {canImpersonate && (
                                  <DropdownMenuItem
                                    disabled={!row.studentId}
                                    onClick={() => openImpersonateConfirm(row)}
                                  >
                                    <LogIn className="mr-2 h-4 w-4" />
                                    View as User
                                  </DropdownMenuItem>
                                )}
                                {!row.sharedKeysEnabled &&
                                  row.releaseStatus === 'released' &&
                                  row.keySource !== 'shared' && (
                                    <DropdownMenuItem onClick={() => setReleaseTarget(row)}>
                                      Update course key
                                    </DropdownMenuItem>
                                  )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled
                              className="invisible pointer-events-none"
                              aria-hidden
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              disabled={
                                !canManage ||
                                !ispring ||
                                applyingExtensionOrderId === row.orderId ||
                                !ispring.isProvisioned
                              }
                              onClick={() => {
                                if (ispring) setApplyExtensionTarget(ispring);
                              }}
                            >
                              {applyingExtensionOrderId === row.orderId
                                ? 'Applying…'
                                : 'Apply paid extension'}
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" aria-label="More actions">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canManage && ispring?.isProvisioned && (
                                  <DropdownMenuItem
                                    disabled={applyingExtensionOrderId === row.orderId}
                                    onClick={() => setApplyExtensionTarget(ispring)}
                                  >
                                    Apply paid extension
                                  </DropdownMenuItem>
                                )}
                                {canManage && (
                                  <DropdownMenuItem
                                    disabled={resendingOrderId === row.orderId}
                                    onClick={() => openResendConfirm(row)}
                                  >
                                    <Mail className="mr-2 h-4 w-4" />
                                    Resend Login Instructions
                                  </DropdownMenuItem>
                                )}
                                {canImpersonate && (
                                  <DropdownMenuItem
                                    disabled={!row.studentId}
                                    onClick={() => openImpersonateConfirm(row)}
                                  >
                                    <LogIn className="mr-2 h-4 w-4" />
                                    View as User
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <MindtapReleaseDialog
        open={Boolean(releaseTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setReleaseTarget(null);
            setReleaseInitialRows(null);
          }
        }}
        studentName={releaseTarget?.studentName || ''}
        mode={releaseTarget?.releaseStatus === 'released' ? 'update' : 'release'}
        initialRows={releaseInitialRows}
        loadingInitial={releaseDialogLoading}
        onConfirm={handleRelease}
      />

      <MindtapEnrollmentPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        loading={previewLoading}
        preview={previewData}
        title={previewTitle}
      />

      <AlertDialog
        open={Boolean(resendTarget)}
        onOpenChange={(open) => !open && !resendingOrderId && setResendTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resend login instructions?</AlertDialogTitle>
            <AlertDialogDescription>
              This sends the class login available email to{' '}
              <span className="font-medium">{resendTarget?.email || 'this student'}</span> for{' '}
              <span className="font-medium">{resendTarget?.programName || 'their program'}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!resendingOrderId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void confirmResendLoginInstructions()}
              disabled={!resendTarget || !!resendingOrderId}
            >
              {resendingOrderId ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send email'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={Boolean(applyExtensionTarget)}
        onOpenChange={(open) => !open && !applyingExtensionOrderId && setApplyExtensionTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {applyExtensionTarget?.platform === 'mindtap'
                ? 'Complete paid MindTap extension?'
                : 'Apply paid iSpring extension?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {applyExtensionTarget?.platform === 'mindtap' ? (
                <>
                  This does not charge again. Confirm that{' '}
                  <span className="font-medium">
                    {applyExtensionTarget.studentName || applyExtensionTarget.email || 'this student'}
                  </span>{' '}
                  has been extended in Cengage. Their course key will show in the portal until the
                  new end date.
                </>
              ) : (
                <>
                  This does not charge again. It looks up the completed Square extension payment for{' '}
                  <span className="font-medium">
                    {applyExtensionTarget?.studentName || applyExtensionTarget?.email || 'this student'}
                  </span>
                  , activates their iSpring login, and sets a new auto-block date.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!applyingExtensionOrderId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void confirmApplyPaidExtension()}
              disabled={!applyExtensionTarget || !!applyingExtensionOrderId}
            >
              {applyingExtensionOrderId ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Applying...
                </>
              ) : applyExtensionTarget?.platform === 'mindtap' ? (
                'Mark extension complete'
              ) : (
                'Activate and extend'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={Boolean(impersonateTarget)}
        onOpenChange={(open) => !open && setImpersonateTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>View as user?</AlertDialogTitle>
            <AlertDialogDescription>
              This opens the student portal in a new tab signed in as{' '}
              <span className="font-medium">
                {impersonateTarget?.studentName || 'this student'}
              </span>
              . Your admin session stays active in this tab. Actions are logged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!impersonatingUserId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void confirmViewAsUser()}
              disabled={!impersonateTarget || !!impersonatingUserId}
            >
              {impersonatingUserId ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Opening...
                </>
              ) : (
                'Open student portal'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
